import { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';

type FileId = string;

interface UseComponentImagesReturn {
  getImageUrl: (fileId?: FileId) => string | undefined;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch and cache image URLs by fileId
 * This helps with properly displaying images when editing components
 */
export const useComponentImages = (
  projectId?: Id<"projects">,
  componentId?: Id<"pageComponents">
): UseComponentImagesReturn => {
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get all files for this component
  const componentFiles = useQuery(
    'filesFunctions:listFilesByComponentFn' as any,
    componentId ? { componentId } : "skip"
  );

  // Get all files for this project as fallback
  const projectFiles = useQuery(
    'filesFunctions:listFilesByProjectFn' as any,
    projectId ? { projectId } : "skip"
  );

  useEffect(() => {
    // Skip if data is not loaded yet
    if ((componentId && componentFiles === undefined) || 
        (projectId && projectFiles === undefined)) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const urls: Record<string, string> = {};
      
      // First check component files
      if (componentFiles && componentFiles.length > 0) {
        componentFiles.forEach(file => {
          if (file._id && file.url) {
            urls[file._id.toString()] = file.url;
          }
        });
      }
      
      // Then check project files
      if (projectFiles && projectFiles.length > 0) {
        projectFiles.forEach(file => {
          // Only add if not already present from component files
          if (file._id && file.url && !urls[file._id.toString()]) {
            urls[file._id.toString()] = file.url;
          }
        });
      }
      
      setImageUrls(urls);
    } catch (err) {

      setError('Failed to load images');
    } finally {
      setIsLoading(false);
    }
  }, [componentFiles, projectFiles, componentId, projectId]);

  // Memoize the getImageUrl function to prevent it from causing infinite renders
  const getImageUrl = useCallback((fileId?: FileId): string | undefined => {
    if (!fileId) return undefined;
    return imageUrls[fileId];
  }, [imageUrls]);

  return {
    getImageUrl,
    isLoading,
    error
  };
}; 